import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import checkUrl from '../../assets/signals/check.svg';
import closeUrl from '../../assets/signals/close.svg';
import pinUrl from '../../assets/signals/pin.svg';
import pinActiveUrl from '../../assets/signals/pin-active.svg';
import type { SignalsQuery } from '../../lib/signals.hooks.js';
import { completenessOf, type Signal, signalCounts, signalsIn } from '../../lib/signals.js';
import { SignalRow } from './SignalRow.js';
import { SignalsButtonLink } from './SignalsLink.js';

export const SIGNALS_PANEL_WIDTH = 400;
const PANEL_GAP = 8;
const PANEL_LIMIT = 7;
// Figma 12055:65097 floating shadow.
const FLOATING_SHADOW = '0 2px 8px rgba(26,31,51,.08), 0 12px 32px rgba(26,31,51,.14)';
const QUIET_BUTTON_SX = {
  width: 32,
  height: 32,
  borderRadius: '8px',
  '&:hover': { bgcolor: 'divider' },
  '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main' },
} as const;
const FOOTER_LINK_SX = {
  p: 0,
  minWidth: 0,
  fontSize: 13,
  fontWeight: 600,
  color: 'primary.main',
  textTransform: 'none',
} as const;

type PanelFilter = 'active' | 'new';

interface SignalsPanelProps {
  query: SignalsQuery;
  docked: boolean;
  // Read once at mount: the user opened it. A pinned panel mounting with its page must not take focus.
  focusOnMount: boolean;
  // Pinning reflows the page, which needs room; below that the panel only floats.
  canPin: boolean;
  // The close button: also unpins. Escape, a click outside or a footer link only dismiss.
  onClose: (restoreFocus: boolean) => void;
  onDismiss: (restoreFocus: boolean) => void;
  onTogglePin: () => void;
  onOpen: (signal: Signal) => void;
  // Clicks here do not count as outside the panel (the rail button that toggles it).
  ignoreOutside?: (target: Node) => boolean;
}

function FilterChip({
  label,
  count,
  selected,
  onClick,
}: {
  label: string;
  count: number;
  selected: boolean;
  onClick: () => void;
}): ReactNode {
  return (
    <ButtonBase
      onClick={onClick}
      aria-pressed={selected}
      sx={{
        gap: '6px',
        px: '12px',
        py: '6px',
        borderRadius: '999px',
        fontSize: 12,
        fontWeight: 600,
        lineHeight: '16px',
        color: selected ? 'primary.main' : 'background.icon',
        bgcolor: selected ? 'primary.bg' : 'background.platform',
        '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main' },
      }}
    >
      {label}
      <Box component="span" sx={{ color: selected ? 'primary.main' : 'text.secondary' }}>
        {count}
      </Box>
    </ButtonBase>
  );
}

function EmptyState({
  title,
  body,
  icon,
}: {
  title: string;
  body: string;
  icon: boolean;
}): ReactNode {
  return (
    <Stack
      data-testid="signals-panel-empty"
      sx={{ flex: 1, alignItems: 'center', justifyContent: 'center', gap: '8px', p: '36px 40px' }}
    >
      {icon ? (
        <Box
          sx={{
            position: 'relative',
            width: 44,
            height: 44,
            borderRadius: '50%',
            bgcolor: 'primary.bg',
            mb: '4px',
          }}
        >
          <Box
            component="img"
            src={checkUrl}
            alt=""
            width={16}
            height={16}
            sx={{ position: 'absolute', left: 14, top: 14 }}
          />
        </Box>
      ) : null}
      <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'background.icon' }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', textAlign: 'center' }}>
        {body}
      </Typography>
    </Stack>
  );
}

export function SignalsPanel({
  query,
  docked,
  focusOnMount,
  canPin,
  onClose,
  onDismiss,
  onTogglePin,
  onOpen,
  ignoreOutside,
}: SignalsPanelProps): ReactNode {
  const [filter, setFilter] = useState<PanelFilter>('active');
  const panel = useRef<HTMLElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const focusHeading = useRef(focusOnMount);
  const { state } = query;
  const counts = signalCounts(query.signals, state);
  const listed = signalsIn(filter, query.signals, state);
  const shown = listed.slice(0, PANEL_LIMIT);
  const completeness =
    query.feed === null ? 'has-active' : completenessOf(query.feed, counts.active);

  useEffect(() => {
    if (focusHeading.current) heading.current?.focus();
  }, []);
  // Following a footer link to HOME's section: a floating panel would cover it.
  const leave = (): void => {
    if (!docked) onDismiss(false);
  };

  // A floating panel closes on Escape or a click outside it; a docked one stays put.
  useEffect(() => {
    if (docked) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onDismiss(true);
    };
    const onPointer = (event: MouseEvent): void => {
      const target = event.target as Node | null;
      if (target === null || panel.current?.contains(target) || ignoreOutside?.(target)) return;
      // A click inside a dialog or menu opened above the page is not a click on the page.
      if ((target as Element).closest?.('.MuiPopover-root, .MuiModal-root')) return;
      onDismiss(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [docked, onDismiss, ignoreOutside]);

  return (
    <Box
      ref={panel}
      component="aside"
      aria-label="Signals"
      data-testid="signals-panel"
      data-pinned={docked}
      sx={(theme) => ({
        position: 'fixed',
        zIndex: 55,
        width: SIGNALS_PANEL_WIDTH,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        '@media print': { display: 'none' },
        ...(docked
          ? {
              left: theme.layout.sidebarWidth,
              top: 0,
              bottom: 0,
              borderRight: '1px solid',
              borderColor: 'divider',
            }
          : {
              left: theme.layout.sidebarWidth + PANEL_GAP,
              top: PANEL_GAP,
              bottom: PANEL_GAP,
              borderRadius: '16px',
              boxShadow: FLOATING_SHADOW,
              border: '1px solid',
              borderColor: 'divider',
            }),
      })}
    >
      <Stack sx={{ p: '20px 16px 12px 24px', gap: '4px' }}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: '8px' }}>
          <Typography
            ref={heading}
            tabIndex={-1}
            component="h2"
            sx={{
              flex: 1,
              fontSize: 20,
              fontWeight: 700,
              lineHeight: '28px',
              color: 'background.icon',
              outline: 'none',
            }}
          >
            Signals
          </Typography>
          <ButtonBase
            onClick={onTogglePin}
            disabled={!canPin}
            aria-pressed={docked}
            aria-label={docked ? 'Unpin signals panel' : 'Pin signals panel'}
            title={canPin ? undefined : 'Widen the window to pin the panel'}
            sx={{
              ...QUIET_BUTTON_SX,
              bgcolor: docked ? 'rgba(21,93,252,.1)' : 'transparent',
              '&.Mui-disabled': { opacity: 0.4 },
            }}
          >
            <Box
              component="img"
              src={docked ? pinActiveUrl : pinUrl}
              alt=""
              width={16}
              height={16}
            />
          </ButtonBase>
          <ButtonBase
            onClick={() => onClose(true)}
            aria-label="Close signals panel"
            sx={QUIET_BUTTON_SX}
          >
            <Box component="img" src={closeUrl} alt="" width={16} height={16} />
          </ButtonBase>
        </Stack>
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.secondary' }}>
          Flagged from your live portfolio
        </Typography>
      </Stack>
      <Stack
        direction="row"
        role="group"
        aria-label="Filter signals"
        sx={{ gap: '8px', p: '0 24px 16px', borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <FilterChip
          label="All active"
          count={counts.active}
          selected={filter === 'active'}
          onClick={() => setFilter('active')}
        />
        <FilterChip
          label="New"
          count={counts.new}
          selected={filter === 'new'}
          onClick={() => setFilter('new')}
        />
      </Stack>

      <Box
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}
      >
        {query.isPending ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: '40px' }}>
            <CircularProgress size={24} aria-label="Loading signals" />
          </Box>
        ) : query.isError ? (
          <Alert
            severity="error"
            sx={{ m: '16px' }}
            action={
              <Button color="inherit" size="small" onClick={query.retry}>
                Retry
              </Button>
            }
          >
            Signals could not be loaded.
          </Alert>
        ) : (
          <>
            {query.refreshFailed || (query.feed?.unreadable ?? 0) > 0 ? (
              <Alert
                severity="warning"
                data-testid="signals-partial"
                sx={{ m: '12px 16px 0', fontSize: 12 }}
                action={
                  <Button color="inherit" size="small" onClick={query.retry}>
                    Retry
                  </Button>
                }
              >
                {query.refreshFailed
                  ? 'Signals could not be refreshed.'
                  : `${query.feed?.unreadable} signal result${query.feed?.unreadable === 1 ? '' : 's'} could not be loaded.`}
              </Alert>
            ) : null}
            {shown.length > 0 ? (
              <Box component="ul" aria-label="Signals list" sx={{ m: 0, p: 0 }}>
                {shown.map((signal) => (
                  <SignalRow
                    key={signal.runId}
                    signal={signal}
                    unread={!state.read.has(signal.runId)}
                    variant="compact"
                    onOpen={onOpen}
                  />
                ))}
              </Box>
            ) : completeness === 'caught-up' ? (
              <EmptyState
                icon
                title="You’re all caught up"
                body="No active signals in any stage. Alison keeps watching your live creatives and shows anything that needs action here."
              />
            ) : completeness === 'incomplete' ? (
              <EmptyState
                icon={false}
                title="Some signals could not be checked"
                body="Nothing active is listed, but not every signal could be read. Retry to check again."
              />
            ) : (
              <EmptyState
                icon={false}
                title="No new signals"
                body="Everything flagged has been opened."
              />
            )}
          </>
        )}
      </Box>

      <Stack
        direction="row"
        sx={{
          p: '14px 24px',
          alignItems: 'center',
          justifyContent: shown.length === 0 ? 'flex-end' : 'space-between',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        {shown.length === 0 ? null : (
          <Typography
            sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary' }}
            data-testid="signals-showing"
          >
            Showing {shown.length} of {listed.length}
          </Typography>
        )}
        {counts.active === 0 ? (
          <SignalsButtonLink
            search={{ signalsTab: 'dismissed' }}
            onClick={leave}
            sx={FOOTER_LINK_SX}
          >
            View dismissed signals
          </SignalsButtonLink>
        ) : (
          <SignalsButtonLink search={{ signalsTab: 'active' }} onClick={leave} sx={FOOTER_LINK_SX}>
            View all signals
          </SignalsButtonLink>
        )}
      </Stack>
    </Box>
  );
}
